import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { notifyConsentChanged, OPEN_COOKIE_EVENT } from "@/lib/consent";
import { X, Cookie, Mail, AlertTriangle, Loader2 } from "lucide-react";

// Renders the three storefront popups (maintenance, cookie, newsletter)
// driven by admin config from GET /public/popups.
export default function StorefrontPopups() {
  const [cfg, setCfg] = useState(null);
  const [showMaint, setShowMaint] = useState(false);
  const [showCookie, setShowCookie] = useState(false);
  const [showNews, setShowNews] = useState(false);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    api.get("/public/popups").then((r) => {
      const c = r.data;
      setCfg(c);
      if (c.maintenance?.enabled && sessionStorage.getItem("sig_maint_dismissed") !== "1") {
        setShowMaint(true);
      }
      if (c.cookie?.enabled && localStorage.getItem("sig_cookie_consent") == null) {
        setShowCookie(true);
      }
      if (c.newsletter?.enabled && localStorage.getItem("sig_news_dismissed") !== "1") {
        const delay = Math.max(0, Number(c.newsletter?.delay ?? 5)) * 1000;
        const t = setTimeout(() => setShowNews(true), delay);
        return () => clearTimeout(t);
      }
    }).catch(() => {});
  }, []);

  const dismissMaint = () => { sessionStorage.setItem("sig_maint_dismissed", "1"); setShowMaint(false); };
  useEffect(() => {
    const open = () => setShowCookie(true);
    window.addEventListener(OPEN_COOKIE_EVENT, open);
    return () => window.removeEventListener(OPEN_COOKIE_EVENT, open);
  }, []);

  const setCookie = (val) => { localStorage.setItem("sig_cookie_consent", val); setShowCookie(false); notifyConsentChanged(); };
  const dismissNews = () => { localStorage.setItem("sig_news_dismissed", "1"); setShowNews(false); };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email"); return; }
    setSubscribing(true);
    try {
      await api.post("/public/newsletter", { email });
      toast.success("You're subscribed! 🎉");
      dismissNews();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not subscribe");
    } finally { setSubscribing(false); }
  };

  if (!cfg) return null;

  return (
    <>
      {/* Maintenance popup */}
      <AnimatePresence>
        {showMaint && (
          <>
            <motion.div className="fixed inset-0 bg-black/50 z-[60] backdrop-blur-sm"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={dismissMaint} />
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 pointer-events-none">
              <motion.div
                className="bg-white rounded-3xl max-w-md w-full p-8 pointer-events-auto text-center"
                initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 26 }}
                data-testid="maintenance-popup"
              >
                <div className="h-14 w-14 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle size={26} />
                </div>
                <h2 className="font-heading font-semibold text-2xl tracking-tight" data-testid="maintenance-title">{cfg.maintenance.title}</h2>
                <p className="font-body text-zinc-500 mt-3 leading-relaxed" data-testid="maintenance-message">{cfg.maintenance.message}</p>
                <button onClick={dismissMaint} data-testid="maintenance-dismiss"
                  className="mt-6 w-full casper-btn rounded-full py-3.5 font-body font-semibold">
                  {cfg.maintenance.button_label || "Got it"}
                </button>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Newsletter popup */}
      <AnimatePresence>
        {showNews && (
          <>
            <motion.div className="fixed inset-0 bg-black/50 z-[55] backdrop-blur-sm"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={dismissNews} />
            <div className="fixed inset-0 z-[55] flex items-center justify-center p-6 pointer-events-none">
              <motion.div
                className="bg-white rounded-3xl max-w-md w-full p-8 pointer-events-auto relative"
                initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 26 }}
                data-testid="newsletter-popup"
              >
                <button onClick={dismissNews} data-testid="newsletter-close"
                  className="absolute top-4 right-4 h-8 w-8 rounded-full bg-zinc-100 flex items-center justify-center hover:bg-zinc-200"><X size={16} /></button>
                <div className="h-14 w-14 rounded-full casper-gradient text-white flex items-center justify-center mb-4">
                  <Mail size={24} />
                </div>
                <h2 className="font-heading font-semibold text-2xl tracking-tight" data-testid="newsletter-title">{cfg.newsletter.title}</h2>
                <p className="font-body text-zinc-500 mt-2 leading-relaxed" data-testid="newsletter-message">{cfg.newsletter.message}</p>
                <form onSubmit={subscribe} className="mt-5 space-y-3">
                  <input
                    data-testid="newsletter-email-input"
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com" autoComplete="email"
                    className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-black"
                  />
                  <button type="submit" disabled={subscribing} data-testid="newsletter-subscribe"
                    className="w-full flex items-center justify-center gap-2 casper-btn rounded-full py-3.5 font-body font-semibold disabled:opacity-60">
                    {subscribing && <Loader2 size={16} className="animate-spin" />} {cfg.newsletter.button_label || "Subscribe"}
                  </button>
                </form>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Cookie popup */}
      <AnimatePresence>
        {showCookie && (
          <motion.div
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-md z-50 bg-white border border-zinc-200 shadow-xl rounded-2xl p-5"
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
            data-testid="cookie-popup"
          >
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-zinc-100 flex items-center justify-center shrink-0">
                <Cookie size={18} />
              </div>
              <p className="font-body text-sm text-zinc-600 leading-relaxed" data-testid="cookie-message">{cfg.cookie.message}</p>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <button onClick={() => setCookie("declined")} data-testid="cookie-decline"
                className="flex-1 text-sm font-semibold border border-zinc-200 rounded-full py-2.5 hover:bg-zinc-50">
                {cfg.cookie.decline_label || "Decline"}
              </button>
              <button onClick={() => setCookie("accepted")} data-testid="cookie-accept"
                className="flex-1 text-sm font-semibold text-white bg-black rounded-full py-2.5 active:scale-95 transition-transform">
                {cfg.cookie.accept_label || "Accept"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
